import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Banner de alertă pentru intrări de ore fără regie/proiect specificat.
 * Admin poate: (1) șterge individual, (2) șterge toate, (3) ATRIBUI manual
 * un proiect sau regie fiecărei intrări orfane (util pentru date istorice
 * unde utilizatorul a uitat/nu a putut selecta activitatea corectă).
 */
export default function OrphanHoursBanner() {
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prList, setPrList] = useState({ projects: [], regii: [] });
  const [rowSel, setRowSel] = useState({}); // { entryId: "P:xxx" or "R:xxx" }

  const load = async () => {
    try {
      const r = await api.get("/admin/orphan-hours");
      setData(r.data);
    } catch { setData(null); }
  };
  const loadPRList = async () => {
    try {
      const r = await api.get("/admin/projects-regii-list");
      setPrList(r.data || { projects: [], regii: [] });
    } catch { /* silent */ }
  };

  useEffect(() => { if (isAdmin) { load(); loadPRList(); } }, [isAdmin]);

  const options = useMemo(() => {
    const opts = [];
    (prList.regii || []).forEach((r) => opts.push({ value: `R:${r.id}`, label: `⚙ ${r.name}` }));
    (prList.projects || []).forEach((p) => opts.push({ value: `P:${p.id}`, label: `🏗 ${p.name}${p.location ? ` — ${p.location}` : ""}` }));
    return opts;
  }, [prList]);

  if (!isAdmin || !data || (data.count || 0) === 0) return null;

  const onDelete = async (id) => {
    if (!window.confirm("Ștergi această intrare orfană?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/orphan-hours/${id}`);
      toast.success("Șters");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setBusy(false); }
  };

  const onDeleteAll = async () => {
    const n = data.count || 0;
    if (!window.confirm(`Ștergi TOATE cele ${n} intrări orfane?\n\nAceastă acțiune este ireversibilă. Datele istorice fără proiect/regie vor fi eliminate din baza de date.`)) return;
    if (!window.confirm(`Confirmă din nou: ștergere DEFINITIVĂ a ${n} intrări.`)) return;
    setBusy(true);
    try {
      const r = await api.delete("/admin/orphan-hours");
      toast.success(`Șterse: ${r.data?.deleted || 0} intrări orfane`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setBusy(false); }
  };

  const onAssign = async (entryId) => {
    const sel = rowSel[entryId];
    if (!sel) { toast.error("Alege un proiect sau regie"); return; }
    const [kind, id] = sel.split(":");
    const body = kind === "P" ? { project_id: id } : { regie_id: id };
    setBusy(true);
    try {
      const r = await api.patch(`/admin/orphan-hours/${entryId}`, body);
      toast.success(`Atribuit: ${r.data?.assigned || "OK"}`);
      setRowSel((s) => { const c = { ...s }; delete c[entryId]; return c; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare atribuire");
    } finally { setBusy(false); }
  };

  const fmtDay = (iso) => {
    try { return new Date(iso + "T00:00:00").toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit", year: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="mb-4 rounded-md border-2 border-orange-400 bg-orange-50 p-3" data-testid="orphan-hours-banner">
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-orange-600 flex-shrink-0 mt-0.5" size={20}/>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-orange-900 text-sm">
            {data.count} intrări de ore fără proiect/regie specificat
          </div>
          <div className="text-xs text-orange-800 mt-0.5">
            Ore introduse ca &bdquo;regie&rdquo; / &bdquo;proiect&rdquo; generic. Poți <b>atribui manual</b> proiectul/regia corect(ă) sau <b>șterge</b> intrările greșite.
            <br/>
            <span className="text-[10px] text-orange-700 italic">Intrările marcate cu &bdquo;<b>import legacy</b>&rdquo; au fost importate din sistemul vechi fără informații despre autor.</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs font-bold text-orange-900 underline"
              data-testid="orphan-hours-toggle"
            >
              {expanded ? "Ascunde detalii" : "Vezi lista"}
            </button>
            <button
              type="button"
              onClick={onDeleteAll}
              disabled={busy}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="orphan-hours-delete-all"
            >
              <Trash2 size={12}/> Șterge TOATE
            </button>
          </div>
          {expanded && (
            <div className="mt-2 max-h-[500px] overflow-auto rounded border border-orange-200 bg-white">
              <table className="w-full text-xs">
                <thead className="bg-orange-100 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">Persoană</th>
                    <th className="text-left px-2 py-1">Data</th>
                    <th className="text-right px-2 py-1">Ore</th>
                    <th className="text-left px-2 py-1">Introdus de</th>
                    <th className="text-left px-2 py-1 min-w-[240px]">Atribuie proiect/regie</th>
                    <th className="text-right px-2 py-1">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.items || []).map((o) => (
                    <tr key={o.id} className="border-t border-orange-100 hover:bg-orange-50" data-testid={`orphan-row-${o.id}`}>
                      <td className="px-2 py-1 font-semibold">{o.person_name}</td>
                      <td className="px-2 py-1 font-mono">{fmtDay(o.date)}</td>
                      <td className="px-2 py-1 font-mono text-right whitespace-nowrap">
                        {(o.hours || 0) > 0 ? `${o.hours}h` : ""}
                        {(o.hours_driven || 0) > 0 ? ` +${o.hours_driven}h dr` : ""}
                      </td>
                      <td className="px-2 py-1 text-[10px] whitespace-nowrap">
                        {o.created_by_name ? (
                          <span className="text-zinc-700">{o.created_by_name}</span>
                        ) : (
                          <span className="italic text-zinc-400" title="Intrare importată din sistemul vechi, fără informații despre autor">import legacy</span>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        <select
                          value={rowSel[o.id] || ""}
                          onChange={(e) => setRowSel((s) => ({ ...s, [o.id]: e.target.value }))}
                          className="w-full text-xs border border-orange-300 rounded px-1 py-0.5 bg-white"
                          data-testid={`orphan-select-${o.id}`}
                        >
                          <option value="">— alege —</option>
                          {options.map((op) => (
                            <option key={op.value} value={op.value}>{op.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => onAssign(o.id)}
                          disabled={busy || !rowSel[o.id]}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 hover:bg-green-200 border border-green-300 text-green-800 disabled:opacity-40 disabled:cursor-not-allowed mr-1"
                          data-testid={`orphan-assign-${o.id}`}
                        >
                          <Check size={11}/> Atribuie
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(o.id)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 hover:bg-red-200 border border-red-300 text-red-800"
                          data-testid={`orphan-delete-${o.id}`}
                        >
                          <Trash2 size={11}/>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
